import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { Loader2, Send, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

interface BatchSendDialogProps {
  open: boolean;
  onClose: () => void;
  type: 'payment' | 'document' | 'departure';
  count: number;
  onSuccess: () => void;
}

export function BatchSendDialog({
  open,
  onClose,
  type,
  count,
  onSuccess,
}: BatchSendDialogProps) {
  const { t } = useTranslation('notifications');
  const [result, setResult] = useState<{
    sent: number;
    failed: number;
  } | null>(null);

  const batchMutation = trpc.notifications.sendBatchNotifications.useMutation({
    onSuccess: (data) => {
      setResult({ sent: data.sent, failed: data.failed });
    },
  });

  const handleSend = async () => {
    await batchMutation.mutateAsync({ type });
  };

  const handleClose = () => {
    setResult(null);
    onClose();
    if (result && result.sent > 0) {
      onSuccess();
    }
  };

  const typeLabel = {
    payment: t('paymentReminders'),
    document: t('documentAlerts'),
    departure: t('departureReminders'),
  };

  return (
    <AlertDialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {result ? (
              result.failed === 0 ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              ) : result.sent === 0 ? (
                <XCircle className="h-5 w-5 text-red-500" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              )
            ) : (
              <Send className="h-5 w-5 text-[hsl(var(--gold))]" />
            )}
            {result ? t('notificationsSent') : t('confirmSendAll')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {result ? (
              <div className="space-y-3 mt-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <span className="text-emerald-700 font-medium">{t('sent')}</span>
                  <span className="text-emerald-700 font-semibold text-lg">{result.sent}</span>
                </div>
                {result.failed > 0 && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-200">
                    <span className="text-red-700 font-medium">{t('failed')}</span>
                    <span className="text-red-700 font-semibold text-lg">{result.failed}</span>
                  </div>
                )}
              </div>
            ) : (
              <>
                {t('confirmSendAllDescription')}
                <div className="mt-4 p-4 rounded-lg bg-[hsl(var(--muted))]">
                  <div className="flex items-center justify-between">
                    <span className="text-[hsl(var(--muted-foreground))]">{typeLabel[type]}</span>
                    <span className="font-semibold text-lg">{count}</span>
                  </div>
                </div>
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {result ? (
            <Button onClick={handleClose}>{t('close')}</Button>
          ) : (
            <>
              <AlertDialogCancel onClick={handleClose}>{t('cancel')}</AlertDialogCancel>
              <Button
                onClick={handleSend}
                disabled={batchMutation.isPending || count === 0}
                className="gap-2"
              >
                {batchMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('sending')}
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    {t('confirm')}
                  </>
                )}
              </Button>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
